import { useState } from 'react';
import { api } from '../api';
import { Card } from '../components/ui';
import { KitjaLogo } from '../components/KitjaLogo';

/**
 * Deliberately minimal (§7). Adults sign in with a magic link; the kitchen
 * tablet never sees this screen because it holds a device token instead.
 */
export function SignInPage({ onSignedIn }: { onSignedIn: () => void }) {
  const [email, setEmail] = useState('');
  // Signing in and starting a household are the same form with a different
  // question, so they share one screen rather than hiding one behind a link.
  const [mode, setMode] = useState<'signIn' | 'signUp'>('signIn');
  const [householdName, setHouseholdName] = useState('');
  const [sent, setSent] = useState(false);
  const [devLink, setDevLink] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mode === 'signUp') {
        const result = await api.signUp(householdName, email);
        // An address that already has a household never gets a second one —
        // it gets signed in, which is what the person meant anyway.
        if (result.existing) {
          setSent(true);
        } else {
          onSignedIn();
        }
        return;
      }
      const result = await api.requestMagicLink(email);
      setSent(true);
      setDevLink(result.devLink ?? null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not send the link');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="main" style={{ maxWidth: 460 }}>
      <div className="stack" style={{ marginTop: '12vh' }}>
        <div style={{ display: 'grid', justifyItems: 'center', gap: 10, marginBottom: 8 }}>
          <KitjaLogo size={72} />
          <h1 style={{ margin: 0 }}>Kitja</h1>
          <p className="muted" style={{ margin: 0 }}>
            What are we eating this week?
          </p>
        </div>

        {/* Better said plainly than discovered three weeks in, when the
            seasons are wrong and the boulangerie is not around the corner. */}
        <p className="dim" style={{ textAlign: 'center', margin: '0 0 2px', lineHeight: 1.5 }}>
          Kitja is built around what is in the shops in France, Alsace especially, with British
          cooking mixed in — a Sunday roast and a good curry alongside the boulangerie run. If you
          shop somewhere else, the seasons and the shops will not quite match.
        </p>

        <Card>
          {sent ? (
            <div className="stack">
              <p className="banner">Check your email for a sign-in link.</p>
              {devLink && (
                <>
                  <p className="dim">No mail server is configured, so here is the link directly:</p>
                  <a
                    className="btn primary"
                    href={devLink}
                    onClick={() => window.setTimeout(onSignedIn, 500)}
                  >
                    Sign in
                  </a>
                </>
              )}
              <button className="btn ghost" onClick={() => setSent(false)}>
                Use a different address
              </button>
            </div>
          ) : (
            <form className="stack" onSubmit={submit}>
              <div className="row" style={{ gap: 6 }}>
                <button
                  type="button"
                  className={`btn small${mode === 'signIn' ? ' on' : ''}`}
                  aria-pressed={mode === 'signIn'}
                  onClick={() => setMode('signIn')}
                >
                  I have an account
                </button>
                <button
                  type="button"
                  className={`btn small${mode === 'signUp' ? ' on' : ''}`}
                  aria-pressed={mode === 'signUp'}
                  onClick={() => setMode('signUp')}
                >
                  Start a household
                </button>
              </div>

              {mode === 'signUp' && (
                <div className="field">
                  <label htmlFor="household">What shall we call your household?</label>
                  <input
                    id="household"
                    required
                    value={householdName}
                    placeholder="The Smiths"
                    onChange={(event) => setHouseholdName(event.target.value)}
                  />
                </div>
              )}

              <div className="field">
                <label htmlFor="email">Email address</label>
                <input
                  id="email"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  placeholder="you@example.com"
                  onChange={(event) => setEmail(event.target.value)}
                />
              </div>
              {error && <p className="banner warn">{error}</p>}
              <button
                className="btn primary"
                type="submit"
                disabled={busy || !email || (mode === 'signUp' && !householdName)}
              >
                {busy
                  ? 'One moment…'
                  : mode === 'signUp'
                    ? 'Start my household'
                    : 'Email me a sign-in link'}
              </button>
            </form>
          )}
        </Card>

        <p className="dim" style={{ textAlign: 'center' }}>
          Setting up the kitchen tablet? Open the device link an adult generated for you.
        </p>
      </div>
    </div>
  );
}
